import React, { useState, useEffect } from 'react';
import * as technicianService from '../../../../services/technicianService.js';

/**
 * ImageField component - handles image file input with compression and preview
 * Images are compressed locally and uploaded when the form is submitted
 */
function ImageField({ field, index, value, onChange, formId }) {
    const fieldId = `${formId}_${field.name}_${index}`;
    const [previews, setPreviews] = useState([]);
    const [isCompressing, setIsCompressing] = useState(false);
    const compressImageToJPG = technicianService.compressImageToJPG;

    // Normalizar value: si viene como Array (ej: recuperado desde cache), convertir a FileList
    // para que el componente se comporte igual tanto con selección del usuario como con cache.
    useEffect(() => {
        if (value && Array.isArray(value) && value.length > 0) {
            // Es un array plano (desde cache); convertir a FileList via DataTransfer
            const dataTransfer = new DataTransfer();
            value.forEach(file => { if (file instanceof File) dataTransfer.items.add(file); });
            if (dataTransfer.files.length > 0) {
                onChange(index, dataTransfer.files);
            }
        }
    // Solo al montar / cuando el tipo de value cambia de Array a FileList
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generar previews cuando cambian los archivos
    useEffect(() => {
        if (value && value.length > 0) {
            const urls = Array.from(value).map(file => ({
                url: URL.createObjectURL(file),
                name: file.name,
                size: file.size
            }));
            setPreviews(urls);

            // Limpiar URLs cuando se desmonte o cambien
            return () => {
                urls.forEach(({ url }) => URL.revokeObjectURL(url));
            };
        } else {
            setPreviews([]);
        }
    }, [value]);

    const handleImageChange = async (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setIsCompressing(true);
            try {
                // Comprimir todas las imágenes
                const compressedFiles = await Promise.all(
                    Array.from(files).map(file => compressImageToJPG(file))
                );

                // Almacenar los archivos comprimidos (se subirán al enviar el formulario)
                const dataTransfer = new DataTransfer();
                compressedFiles.forEach(file => dataTransfer.items.add(file));
                onChange(index, dataTransfer.files);
            } catch (error) {
                console.error('Error al comprimir imágenes:', error);
                onChange(index, files);
            } finally {
                setIsCompressing(false);
            }
        } else {
            onChange(index, null);
        }
    };

    const removeImage = (indexToRemove) => {
        if (value && value.length > 0) {
            const dataTransfer = new DataTransfer();
            Array.from(value).forEach((file, idx) => {
                if (idx !== indexToRemove) {
                    dataTransfer.items.add(file);
                }
            });

            // Si quedan archivos, actualizar, sino pasar null
            if (dataTransfer.files.length > 0) {
                onChange(index, dataTransfer.files);
            } else {
                onChange(index, null);
            }
        }
    };

    const hasImages = value && value.length > 0;

    return (
        <div className="space-y-3">
            {/* Input de archivo con diseño minimalista de línea punteada */}
            <div className="relative">
                <input
                    type="file"
                    id={fieldId}
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    disabled={isCompressing}
                />
                <label
                    htmlFor={fieldId}
                    className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-line rounded-lg cursor-pointer hover:border-muted-deep hover:bg-surface-2 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2 text-content-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-content-muted">
                        {hasImages
                            ? `${value.length} imagen${value.length !== 1 ? 'es' : ''} seleccionada${value.length !== 1 ? 's' : ''}`
                            : 'Haz clic para seleccionar imágenes'
                        }
                    </span>
                </label>
            </div>

            {/* Indicador de procesamiento (compresión / conversión HEIC) */}
            {isCompressing && (
                <div className="text-sm text-accent-text flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Procesando imágenes...
                </div>
            )}

            {/* Preview de imágenes comprimidas */}
            {hasImages && !isCompressing && (
                <div>
                    <p className="text-sm text-content mb-3">
                        {value.length} {value.length === 1 ? 'imagen seleccionada' : 'imágenes seleccionadas'} (comprimidas):
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {previews.map((preview, fileIndex) => (
                            <div key={fileIndex} className="relative group">
                                <div className="aspect-square rounded-lg overflow-hidden bg-surface-2 border border-line">
                                    <img
                                        src={preview.url}
                                        alt={`Preview ${fileIndex + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                {/* Botón para eliminar imagen */}
                                <button
                                    type="button"
                                    onClick={() => removeImage(fileIndex)}
                                    className="absolute top-1 right-1 bg-error hover:bg-error/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                                    title="Eliminar imagen"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>

                                {/* Info en hover */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center rounded-lg pointer-events-none">
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-center px-2">
                                        <p className="text-xs font-medium truncate">{preview.name}</p>
                                        <p className="text-xs mt-1">{(preview.size / 1024).toFixed(1)} KB</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Texto informativo */}
            <p className="text-xs text-accent-text flex items-center gap-1">
                <span>💡</span>
                <span>Las imágenes se comprimen automáticamente y se subirán al enviar el formulario.</span>
            </p>
        </div>
    );
}

export default ImageField;
